import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Calendar, ChevronDown } from 'lucide-react'

export interface DateRange {
  start_date: string | null
  end_date: string | null
  preset: string
}

const DEFAULT_PRESETS = [
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'quarter_to_date', label: 'Quarter to Date' },
  { key: 'year_to_date', label: 'Year to Date' },
  { key: 'all_time', label: 'All Time' },
]

const ALL_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'last_7_days', label: 'Last 7 Days' },
  { key: 'last_30_days', label: 'Last 30 Days' },
  { key: 'last_90_days', label: 'Last 90 Days' },
  ...DEFAULT_PRESETS,
]

interface Props {
  dateRange: DateRange
  path: string
  presets?: { key: string; label: string }[]
  extraParams?: Record<string, string | number | undefined | null>
}

export function getDateLabel(dateRange: DateRange) {
  const preset = ALL_PRESETS.find(p => p.key === dateRange.preset)
  if (dateRange.preset === 'custom' && dateRange.start_date && dateRange.end_date) {
    const start = new Date(dateRange.start_date + 'T00:00:00')
    const end = new Date(dateRange.end_date + 'T00:00:00')
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
  }
  return preset?.label || 'All Time'
}

export default function DateRangeFilter({ dateRange, path, presets, extraParams }: Props) {
  const [showPicker, setShowPicker] = useState(false)
  const [customStart, setCustomStart] = useState(dateRange.start_date || '')
  const [customEnd, setCustomEnd] = useState(dateRange.end_date || '')

  const presetList = presets || DEFAULT_PRESETS

  function buildParams(extra: Record<string, string | number | undefined | null>) {
    const out: Record<string, string | number> = {}
    if (extraParams) {
      Object.entries(extraParams).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') out[k] = v })
    }
    Object.entries(extra).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') out[k] = v })
    return out
  }

  function applyPreset(preset: string) {
    setShowPicker(false)
    router.get(path, buildParams({ preset }), { preserveState: false })
  }

  function applyCustomRange() {
    if (customStart && customEnd) {
      setShowPicker(false)
      router.get(path, buildParams({ preset: 'custom', start_date: customStart, end_date: customEnd }), { preserveState: false })
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowPicker(!showPicker)}
        className="flex items-center gap-2 px-4 py-2.5 bg-white border border-chestnut-gray rounded-lg hover:border-chestnut-forest/40 transition-colors text-sm font-medium text-chestnut-charcoal shadow-sm"
      >
        <Calendar className="h-4 w-4 text-chestnut-forest" />
        <span>{getDateLabel(dateRange)}</span>
        <ChevronDown className="h-4 w-4 text-chestnut-charcoal/40" />
      </button>

      {showPicker && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowPicker(false)} />
          <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-chestnut-gray z-50 overflow-hidden">
            <div className="p-2 max-h-72 overflow-y-auto">
              {presetList.map((preset) => (
                <button
                  key={preset.key}
                  onClick={() => applyPreset(preset.key)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    dateRange.preset === preset.key
                      ? 'bg-chestnut-forest text-white font-medium'
                      : 'text-chestnut-charcoal hover:bg-chestnut-cream'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <div className="border-t border-chestnut-gray p-3 bg-chestnut-cream/50">
              <p className="text-xs font-medium text-chestnut-charcoal/60 uppercase tracking-wide mb-2">Custom Range</p>
              <div className="flex gap-2 mb-2">
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="flex-1 px-2 py-1.5 border border-chestnut-charcoal/20 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-chestnut-forest"
                />
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="flex-1 px-2 py-1.5 border border-chestnut-charcoal/20 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-chestnut-forest"
                />
              </div>
              <button
                onClick={applyCustomRange}
                disabled={!customStart || !customEnd}
                className="w-full px-3 py-1.5 bg-chestnut-forest text-white text-sm font-medium rounded-lg hover:bg-chestnut-forest/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Apply Range
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
